import React, { useMemo, useCallback } from "react";
import { View } from "react-native";
import * as yup from "yup";
import { z as zod } from "zod";
import { Formik, useFormContext, isEvent, FieldContext, } from "@clearabee/ui-library-base";
import { styles } from "./Field.styles";
import { Text } from "../../Text/Text";
import { Message } from "../../Message/Message";
export const Field = ({ children, label: userLabel, required: userRequired, styles: userStyles, labelStyles, errorBackground, errorStyles, invertErrorColors, ...fieldProps }) => {
    const formContext = useFormContext();
    if (!formContext) {
        throw new Error("Cannot use Field outside of a Form provider");
    }
    const { validationSchema } = formContext;
    const { label, required } = useMemo(() => {
        const values = { label: userLabel, required: userRequired };
        if (!validationSchema)
            return values;
        if (validationSchema instanceof zod.Schema) {
            const zodSchemaShape = validationSchema.shape;
            if (values.required === undefined)
                values.required = !zodSchemaShape[fieldProps.name].isOptional();
            return values;
        }
        let schemaObject;
        try {
            schemaObject = yup.reach(validationSchema, fieldProps.name).describe();
        }
        catch {
            console.error(`${fieldProps.name} does not exist on the form schema`);
        }
        finally {
            if (!schemaObject)
                return values;
        }
        const isFieldRequired = !!schemaObject.tests.find((item) => item.name === "required");
        if (values.label === undefined && schemaObject.label)
            values.label = schemaObject.label;
        if (values.required === undefined)
            values.required = isFieldRequired;
        return values;
    }, [validationSchema, userLabel, userRequired]);
    const renderChildren = useCallback((field) => {
        if (typeof children !== "function") {
            return children;
        }
        const { form: { setFieldValue, setFieldTouched }, } = field;
        const newField = field;
        if (newField.meta.touched) {
            newField.field.error = field.meta.error;
        }
        newField.field.onChange = (value) => {
            setFieldValue(field.field.name, isEvent(value) ? value.target.value : value);
        };
        newField.field.onBlur = () => setFieldTouched(field.field.name, true);
        return children(newField);
    }, [children]);
    return (React.createElement(Formik.Field, { ...fieldProps }, (field) => (React.createElement(FieldContext.Provider, { value: { isField: true } },
        React.createElement(View, { testID: "field-component-wrapper", style: [styles.field, userStyles] },
            label && (React.createElement(Text, { styles: [styles.label, labelStyles] },
                label,
                required && "*")),
            renderChildren(field),
            field.meta.error && field.meta.touched && (React.createElement(Message, { type: "error", small: true, color: invertErrorColors ? "light" : undefined, background: errorBackground, testID: "error-message", styles: [
                    styles.message,
                    invertErrorColors && styles.invertErrorColors,
                    errorStyles,
                ] }, field.meta.error)))))));
};
//# sourceMappingURL=Field.js.map